import { motion } from 'framer-motion';

interface ProgressBarProps {
  value: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'xp';
  showLabel?: boolean;
  label?: string;
  animate?: boolean;
}

const sizeStyles = {
  sm: 'h-1.5',
  md: 'h-2.5',
  lg: 'h-4',
};

const colorStyles = {
  primary: 'bg-otter-500',
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  danger: 'bg-red-500',
  xp: 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500',
};

const bgStyles = {
  primary: 'bg-otter-100 dark:bg-otter-900',
  success: 'bg-green-100 dark:bg-green-900',
  warning: 'bg-yellow-100 dark:bg-yellow-900',
  danger: 'bg-red-100 dark:bg-red-900',
  xp: 'bg-amber-100 dark:bg-amber-900',
};

export function ProgressBar({
  value,
  max = 100,
  size = 'md',
  color = 'primary',
  showLabel = false,
  label,
  animate = true,
}: ProgressBarProps) {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  return (
    <div className="w-full">
      {(showLabel || label) && (
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
            {label}
          </span>
          {showLabel && (
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
              {Math.round(percentage)}%
            </span>
          )}
        </div>
      )}
      <div
        className={`
          w-full rounded-full overflow-hidden
          ${bgStyles[color]}
          ${sizeStyles[size]}
        `}
      >
        <motion.div
          initial={animate ? { width: 0 } : { width: `${percentage}%` }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className={`
            h-full rounded-full
            ${colorStyles[color]}
          `}
        />
      </div>
    </div>
  );
}

interface XpBarProps {
  currentXp: number;
  levelXp: number;
  nextLevelXp: number;
  level: number;
  levelName: string;
}

export function XpBar({ currentXp, levelXp, nextLevelXp, level, levelName }: XpBarProps) {
  const xpInLevel = currentXp - levelXp;
  const xpNeeded = nextLevelXp - levelXp;
  const percentage = (xpInLevel / xpNeeded) * 100;

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
            Lv. {level}
          </span>
          <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
            {levelName}
          </span>
        </div>
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">
          {xpInLevel.toLocaleString()} / {xpNeeded.toLocaleString()} XP
        </span>
      </div>
      <ProgressBar value={percentage} color="xp" size="lg" />
    </div>
  );
}
